import type {
  HealthLogMeasurement,
  HealthLogMetricDefinition,
} from '@/services/graphqlHooks';
import healthLogMetrics from '@/translation/healthLogMetrics';
import healthLogMetricFields from '@/translation/healthLogMetricsFields';

/***********************
 * Parsing
 ***********************/

export const parseHealthLogValues = (
  rawValues: HealthLogMeasurement['values']
): Record<string, number> => {
  if (!rawValues) return {};

  const parsed: unknown =
    typeof rawValues === 'string'
      ? (() => {
          try {
            return JSON.parse(rawValues) as unknown;
          } catch {
            return {};
          }
        })()
      : rawValues;

  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};

  return Object.entries(parsed as Record<string, unknown>).reduce<
    Record<string, number>
  >((acc, [key, value]) => {
    const num = typeof value === 'number' ? value : Number(value);
    return Number.isNaN(num) ? acc : {...acc, [key]: num};
  }, {});
};

/***********************
 * Display helpers (for table)
 ***********************/

export type HealthLogDisplayValue = {
  name: string;
  value: number;
  unit: string | null;
};

export const formatHealthLogValuesForDisplay = (
  measurement: HealthLogMeasurement,
  metricDefinition?: HealthLogMetricDefinition
): HealthLogDisplayValue[] => {
  const values = parseHealthLogValues(measurement.values);

  if (!metricDefinition) {
    return Object.entries(values).map(([name, value]) => ({
      name,
      value,
      unit: null,
    }));
  }

  return metricDefinition.fields
    .map<HealthLogDisplayValue | null>(field => {
      const value = values[field.name];
      if (value == null) return null;

      const name = healthLogMetricFields[field.name] || field.name;

      return {
        name,
        value,
        unit: field.unit ?? null,
      };
    })
    .filter((v): v is HealthLogDisplayValue => v !== null);
};

export const getMetricLabel = (metricId: string): string =>
  healthLogMetrics[metricId] || metricId;

/***********************
 * Chart helpers
 ***********************/

export type HealthLogSeriesPoint = {
  x: Date;
  y: number;
};

export type HealthLogSeries = {
  fieldName: string;
  label: string;
  unit: string | null;
  points: HealthLogSeriesPoint[];
};

export const buildHealthLogSeries = (
  measurements: HealthLogMeasurement[],
  metricDefinition: HealthLogMetricDefinition
): HealthLogSeries[] => {
  const fieldNames = metricDefinition.fields.map(f => f.name);

  const seriesByField = fieldNames.reduce<Record<string, HealthLogSeries>>(
    (acc, fieldName) => {
      const def = metricDefinition.fields.find(f => f.name === fieldName)!;
      const label = healthLogMetricFields[fieldName] || fieldName;

      return {
        ...acc,
        [fieldName]: {
          fieldName,
          label: def.unit ? `${label} (${def.unit})` : label,
          unit: def.unit ?? null,
          points: [],
        },
      };
    },
    {}
  );

  const withPoints = measurements.reduce<Record<string, HealthLogSeries>>(
    (acc, m) => {
      const parsedValues = parseHealthLogValues(m.values);
      const x = new Date(m.recordedAt);

      return Object.entries(acc).reduce<Record<string, HealthLogSeries>>(
        (innerAcc, [fieldName, series]) => {
          const value = parsedValues[fieldName];
          if (value == null) return innerAcc;

          return {
            ...innerAcc,
            [fieldName]: {
              ...series,
              points: [...series.points, {x, y: value}],
            },
          };
        },
        acc
      );
    },
    seriesByField
  );

  return Object.values(withPoints);
};
